import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutGrid, Users, Newspaper, Share2, LogOut, Megaphone, Gift, CalendarRange, Mail, NotebookPen, Receipt, Clapperboard, BarChart3, PenLine,
} from "lucide-react";
import ReaderStats from "@/components/admin/ReaderStats";
import VideoDesk from "@/components/admin/VideoDesk";
import Overview from "@/components/admin/Overview";
import Members from "@/components/admin/Members";
import Editions from "@/components/admin/Editions";
import Articles from "@/components/admin/Articles";
import Notices from "@/components/admin/Notices";
import Partners from "@/components/admin/Partners";
import Social from "@/components/admin/Social";
import Newsletter from "@/components/admin/Newsletter";
import Leads from "@/components/admin/Leads";
import Credits from "@/components/admin/Credits";

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [openMemberId, setOpenMemberId] = useState(null);
  const gotoMember = (id) => { setOpenMemberId(id); setTab("members"); };
  const gotoNewsletter = () => setTab("newsletter");

  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/studio");
    else if (user.role !== "admin") navigate("/portal", { replace: true });
  }, [loading, user, navigate]);

  if (loading || !user || user.role !== "admin") return <div className="min-h-screen flex items-center justify-center bg-ink text-cream"><p className="overline">Loading…</p></div>;

  const NAV = [
    { id: "overview", label: "Overview", icon: LayoutGrid },
    { id: "readers", label: "Readers", icon: BarChart3 },
    { id: "members", label: "Members", icon: Users },
    { id: "editions", label: "Editions", icon: CalendarRange },
    { id: "newsletter", label: "Newsletter", icon: Mail },
    { id: "newsroom", label: "Newsroom", icon: Newspaper },
    { id: "articles", label: "Member Pieces", icon: PenLine },
    { id: "social", label: "Social", icon: Share2 },
    { id: "videodesk", label: "Video Desk", icon: Clapperboard },
    { id: "leads", label: "Leads", icon: NotebookPen },
    { id: "credits", label: "Credits", icon: Receipt },
  ];
  const NAV_SMALL = [
    { id: "notices", label: "Noticeboard", icon: Megaphone },
    { id: "partners", label: "Partners", icon: Gift },
  ];

  return (
    <div className="min-h-screen bg-cream flex">
      <aside className="w-[78px] lg:w-[240px] bg-ink text-cream flex flex-col shrink-0 sticky top-0 h-screen grain">
        <div className="px-5 lg:px-7 py-7 relative z-10">
          <span className="font-serif text-2xl hidden lg:block">S/ME</span>
          <span className="font-serif text-2xl lg:hidden">S/</span>
          <p className="overline text-gloss-gold mt-1 hidden lg:block">Editor Studio</p>
        </div>
        <nav className="flex-1 px-3 lg:px-4 relative z-10 space-y-1">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)}
              className={`w-full flex items-center gap-3 px-3 lg:px-4 py-3 transition-colors ${tab === n.id ? "bg-cream text-ink" : "hover:bg-cream/10"}`}
              data-testid={`admin-nav-${n.id}`}>
              <n.icon size={18} strokeWidth={1.5} />
              <span className="overline hidden lg:block">{n.label}</span>
            </button>
          ))}
          <div className="pt-4 mt-4 border-t border-cream/15">
            <p className="overline text-cream/35 px-3 lg:px-4 pb-1 hidden lg:block">The small stuff</p>
            {NAV_SMALL.map((n) => (
              <button key={n.id} onClick={() => setTab(n.id)}
                className={`w-full flex items-center gap-3 px-3 lg:px-4 py-2.5 transition-colors ${tab === n.id ? "bg-cream text-ink" : "text-cream/70 hover:bg-cream/10 hover:text-cream"}`}
                data-testid={`admin-nav-${n.id}`}>
                <n.icon size={16} strokeWidth={1.5} />
                <span className="overline hidden lg:block">{n.label}</span>
              </button>
            ))}
          </div>
        </nav>
        <button onClick={() => { logout(); navigate("/"); }} className="px-3 lg:px-4 py-5 relative z-10 flex items-center gap-3 hover:bg-cream/10 transition-colors" data-testid="admin-logout">
          <LogOut size={18} strokeWidth={1.5} /> <span className="overline hidden lg:block">Sign out</span>
        </button>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        {tab === "overview" && <Overview onOpenMember={gotoMember} onOpenNewsletter={gotoNewsletter} />}
        {tab === "members" && <Members openId={openMemberId} onConsumed={() => setOpenMemberId(null)} />}
        {tab === "editions" && <Editions />}
        {tab === "newsletter" && <Newsletter />}
        {tab === "newsroom" && <Articles scope="newsroom" />}
        {tab === "articles" && <Articles scope="members" />}
        {tab === "social" && <Social />}
        {tab === "videodesk" && <VideoDesk />}
        {tab === "leads" && <Leads />}
        {tab === "credits" && <Credits />}
        {tab === "readers" && <ReaderStats />}
        {tab === "notices" && <Notices />}
        {tab === "partners" && <Partners />}
      </main>
    </div>
  );
}
